import React, { useMemo } from 'react';
import { Menu, Badge, Avatar, Dropdown, Space, Grid } from 'antd';
import type { MenuProps } from 'antd';
import {
  HomeOutlined,
  ShoppingOutlined,
  DollarOutlined,
  SafetyCertificateOutlined,
  ProjectOutlined,
  SettingOutlined,
  ShopOutlined,
  ToolOutlined,
  TeamOutlined,
  ReadOutlined,
  BarChartOutlined,
  BellOutlined,
  UserOutlined,
  LogoutOutlined,
  RocketOutlined,
  GoldOutlined,
  EllipsisOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@/contexts/NavigationContext';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { resolvePublicAssetUrl } from '@/config/apiBaseUrl';
import { useUnreadNotificationCount } from '@/hooks/useUnreadNotificationCount';
import { useVisibleNavigationModules } from '@/hooks/useVisibleNavigationModules';
import type { NavModuleKey } from '@/config/navigationModules';
import { useProductBrand } from '@/hooks/useProductBrand';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import styles from './TopNavigationBar.module.css';

const MODULE_ICONS: Record<NavModuleKey, React.ReactNode> = {
  home: <HomeOutlined />,
  sales: <ShoppingOutlined />,
  scm: <ShopOutlined />,
  mfg: <ToolOutlined />,
  asset: <GoldOutlined />,
  fin: <DollarOutlined />,
  qms: <SafetyCertificateOutlined />,
  prj: <ProjectOutlined />,
  epc: <ProjectOutlined />,
  hr: <TeamOutlined />,
  edu: <ReadOutlined />,
  analytics: <BarChartOutlined />,
  ownerIntel: <RocketOutlined />,
  platform: <SettingOutlined />,
};

interface TopNavigationBarProps {
  isMobile?: boolean;
  /** MODERN UPGRADE: Tablet (768–991px) spacing between mobile and desktop */
  isTablet?: boolean;
}

export const TopNavigationBar: React.FC<TopNavigationBarProps> = ({
  isMobile = false,
  isTablet = false,
}) => {
  const screens = Grid.useBreakpoint();
  /** iPad landscape (~1024) is lg but still tight for a full module strip. */
  const isCompactNav = isMobile || isTablet || !screens.xl;
  const { t } = useTranslation(['nav', 'common']);
  const { activeModule, setActiveModule } = useNavigation();
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();
  const { data: unreadNotificationCount = 0 } = useUnreadNotificationCount();
  const headerAvatarSrc = resolvePublicAssetUrl(user?.avatar_url);
  const { visibleModuleList, getLandingRoute } = useVisibleNavigationModules();
  const brand = useProductBrand();

  const mainModules: MenuProps['items'] = useMemo(
    () =>
      visibleModuleList.map((mod) => {
        const full = t(mod.key, { ns: 'nav', defaultValue: mod.label });
        const compact = t(`compact.${mod.key}`, { ns: 'nav', defaultValue: full });
        const label = isCompactNav ? compact : full;
        return {
          key: mod.key,
          label,
          icon: MODULE_ICONS[mod.key],
          title: full,
        };
      }),
    [visibleModuleList, isCompactNav, t]
  );

  const handleModuleClick = ({ key }: { key: string }) => {
    setActiveModule(key);
    navigate(getLandingRoute(key as NavModuleKey));
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'language',
      label: (
        <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <LanguageSwitcher compact showLabel />
        </div>
      ),
    },
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('profile', { ns: 'common' }),
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('settings', { ns: 'common' }),
      onClick: () => navigate('/platform/setup/company'),
    },
    {
      key: 'preferences',
      icon: <SettingOutlined />,
      label: t('preferences', { ns: 'common' }),
      onClick: () => navigate('/platform/preferences'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('logout', { ns: 'common' }),
      onClick: logout,
    },
  ];

  const navPad = isMobile ? '0 12px' : isCompactNav ? '0 16px' : '0 24px';
  const logoGap = isMobile ? '12px' : isCompactNav ? '16px' : '32px';

  return (
    <div
      className={`top-navigation-bar ${styles.root} ${isCompactNav ? styles.compact : ''}`}
      style={{
        borderBottom: '1px solid #f0f0f0',
        background: '#fff',
        display: 'flex',
        alignItems: 'center',
        padding: navPad,
        height: '50px',
        minHeight: '50px',
        gap: isCompactNav ? 8 : 12,
      }}
    >
      <div
        className="top-nav-logo-home"
        role="link"
        tabIndex={0}
        aria-label={`${brand.name} home`}
        onClick={() => navigate('/')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate('/');
          }
        }}
        style={{
          cursor: 'pointer',
          marginRight: logoGap,
          flexShrink: 0,
          minWidth: isMobile ? '88px' : '108px',
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span
          className="top-nav-ordan-wordmark"
          style={{
            fontFamily: "'Syne', 'Segoe UI', system-ui, sans-serif",
            fontWeight: 800,
            fontSize: isMobile ? '16px' : '18px',
            letterSpacing: '-0.03em',
            color: brand.color,
            textTransform: 'uppercase',
            lineHeight: 1,
          }}
        >
          {brand.name}
        </span>
      </div>

      {/*
        Ant Design horizontal overflow only works when the flex child can shrink
        (minWidth: 0). Without this, trailing modules clip off-screen on iPad.
      */}
      <div className={styles.menuShell}>
        <Menu
          mode="horizontal"
          selectedKeys={[activeModule]}
          items={mainModules}
          onClick={handleModuleClick}
          overflowedIndicator={
            <span className={styles.moreIndicator} aria-label={t('moreModules', { ns: 'common' })}>
              <EllipsisOutlined />
              <span className={styles.moreLabel}>{t('moreModules', { ns: 'common' })}</span>
            </span>
          }
          style={{
            flex: 'auto',
            minWidth: 0,
            width: '100%',
            borderBottom: 'none',
            fontSize: isCompactNav ? '13px' : '15px',
            fontWeight: 500,
            lineHeight: '50px',
            background: 'transparent',
          }}
        />
      </div>

      <Space
        size={isCompactNav ? 'small' : 'middle'}
        className={styles.actions}
        style={{ marginLeft: isCompactNav ? 8 : 16, flexShrink: 0 }}
      >
        {!isMobile && <LanguageSwitcher compact />}
        <Badge count={unreadNotificationCount} showZero={false} overflowCount={99} offset={[-5, 5]}>
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate('/home/alerts')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/home/alerts');
              }
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: '#595959',
              minWidth: 44,
              minHeight: 44,
              justifyContent: 'center',
            }}
            aria-label={t('notifications', { ns: 'common' })}
          >
            <BellOutlined style={{ fontSize: isCompactNav ? '18px' : '20px' }} />
          </span>
        </Badge>

        <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
          <Space
            style={{ cursor: 'pointer', minHeight: 44, alignItems: 'center' }}
            size="small"
          >
            <Avatar
              src={headerAvatarSrc}
              icon={<UserOutlined aria-hidden />}
              style={headerAvatarSrc ? undefined : { backgroundColor: '#145a54' }}
              size="default"
            />
            {/* Hide email until xl so nav modules keep room on iPad / small laptops */}
            {!isCompactNav && user?.email && (
              <span
                style={{
                  fontSize: '14px',
                  color: '#595959',
                  maxWidth: '140px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user.email.split('@')[0]}
              </span>
            )}
          </Space>
        </Dropdown>
      </Space>
    </div>
  );
};
